import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl, FormGroupDirective, NgForm } from '@angular/forms';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { ErrorStateMatcher } from '@angular/material/core';

import { ApplicantService, AuthService, FileService, JobService, OptInService, ResumeService, SharedUtilsService, UserService } from '@app/services';
import { Applicant } from '@app/models/applicant';
import { Job, User, OptIn } from '@app/models';
import { Router } from '@node_modules/@angular/router';

declare const gtag: Function;

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'apply-form-with-profession',
  templateUrl: './apply-form-with-profession.component.html',
  styleUrl: './apply-form-with-profession.component.scss',
  standalone: false
})
export class ApplyFormWithProfessionComponent implements OnInit {
  @Input() applicationType: string; // ..eg: apply, inquire
  @Input() jobPassedIn: Job;
  @Input() userPassedIn?: User;
  @Input() referringSrc?: string = null;
  @Input() hideProfessionPicker?: boolean = false;

  verifiedCaptcha: boolean = false;
  recaptchaSiteKey: string = '6Le0PxwTAAAAAH5fQTQNMMF5wv5RPz8LtwdBzyPY';
  
  phoneMask = null;

  currentApplicant: Applicant = null;
  applyForm: UntypedFormGroup = null;
  formChecked: boolean = false;
  validForm: boolean = true;
  optIntoSms: boolean = false;
  optIntoEmailPhone: boolean = false;  

  filteredJobTitles = []; // Filtered list

  isLoadingResults: boolean = false;

  emailControl: FormControl = null;
  phoneControl: FormControl = null;
  zipControl: FormControl = null;
  firstNameControl: FormControl = null;
  lastNameControl: FormControl = null;
  professionControl: FormControl = null;
  resumeFormControl: FormControl = null;

  readonly SMS_OPT_IN_CONSENT_LANGUAGE = 'By checking, you consent to receive SMS messages from EDU Healthcare regarding job opportunities and related services at the provided number. Message and data rates may apply. You can opt out at any time by replying STOP or UNSUBSCRIBE to any message.';
  readonly EMAIL_PHONE_OPT_IN_CONSENT_LANGUAGE = 'By checking, you consent to receive phone calls and emails from EDU Healthcare regarding job opportunities and related services.';

  uploadPath: string = 'resumes';
  @ViewChild('file', {static: false}) file;
  public files: Set<File> = new Set();

  filesAdded: boolean = false;
  fileName: string;
  resumePathToUse: string = null; 
  
  currentUser: User = null;

  matcher = new MyErrorStateMatcher();

  constructor(
    private _router: Router,
    private _authService: AuthService,
    private fb: UntypedFormBuilder,
    private _sharedUtilsService: SharedUtilsService,
    private _jobService: JobService,
    private _fileService: FileService,
    private _resumeService: ResumeService,  
    private _optInService: OptInService,
    private _userService: UserService,  
    private _applicantService: ApplicantService
  ) {
    this.phoneMask = this._sharedUtilsService.phoneMask;
  }  


  ngOnInit(): void {
    this.filteredJobTitles = this._jobService.jobList;
    this.recaptchaSiteKey = this._applicantService.siteKey;
    this.initForm();
  }
  

  async handleOptIn(user: User) {
    try {
      // Check if user already has an opt-in record
      const existingOptIn = await this._optInService.getByEmail(this.currentUser.email).toPromise();

      if (!existingOptIn) {
        // No existing opt-in, create one. Confirmation email is sent server-side.
        const optIn = new OptIn();
        optIn.user = user._id || user;
        optIn.email = this.currentUser.email;
        optIn.phone = this.currentUser.phone;
        optIn.smsOptIn = this.optIntoSms;
        optIn.emailPhoneOptIn = this.optIntoEmailPhone;
        optIn.consentLanguage = [
          this.optIntoSms ? this.SMS_OPT_IN_CONSENT_LANGUAGE : null,
          this.optIntoEmailPhone ? this.EMAIL_PHONE_OPT_IN_CONSENT_LANGUAGE : null
        ].filter(Boolean).join(' ');

        await this._optInService.create(optIn).toPromise();
      }
    } catch (err) {
      console.log('Error handling opt-in: ', err);
    }
  }  


  initForm() {
    this.applyForm = this.fb.group({
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, [Validators.required]],
      zip: [null, [Validators.required]],
      profession: [null, Validators.required],
      message: [null],
      resume: [null, Validators.required],
      recaptchaField: [null]
    });

    if (this.userPassedIn && this.userPassedIn._id !== undefined) {
      this.currentUser = {...this.userPassedIn};
      this.checkForUserResumes();
    } else {
      this.currentUser = new User();
    }    

    if (this.currentUser.first_name && this.currentUser.first_name === '-') {
      this.currentUser.first_name = '';
    }

    if (this.currentUser.last_name && this.currentUser.last_name === '-') {
      this.currentUser.last_name = '';
    }

    if (this.currentUser && this.currentUser.first_name?.length) {
      this.applyForm.patchValue({'first_name': this.currentUser.first_name});
      this.applyForm.patchValue({'last_name': this.currentUser.last_name});
    }

    this.currentApplicant = new Applicant();
    this.currentApplicant.applicationType = this.applicationType;
    this.currentApplicant.job = this.jobPassedIn;    

    this.emailControl = this.applyForm.get('email') as FormControl;
    this.phoneControl = this.applyForm.get('phone') as FormControl;
    this.zipControl = this.applyForm.get('zip') as FormControl;
    this.firstNameControl = this.applyForm.get('first_name') as FormControl;
    this.lastNameControl = this.applyForm.get('last_name') as FormControl;
    this.professionControl = this.applyForm.get('profession') as FormControl;
    this.resumeFormControl = this.applyForm.get('resume') as FormControl;
  }


  async checkForUserResumes() {
    const userResumes = await this._resumeService.getByUser(this.userPassedIn._id).toPromise();
    if (userResumes && userResumes !== undefined) this.currentUser.resumes = userResumes;
  }  


  addFiles() {
    this.files = new Set();
    this.filesAdded = false;
    this.fileName = null;
    this.file.nativeElement.value = "";

    if (this.currentApplicant.resume_path) delete this.currentApplicant.resume_path; // User is selecting a different file, removing this
    
    this.file.nativeElement.click();
  }


  onFilesAdded() {
    this.filesAdded = true;

    const files: { [key: string]: File } = this.file.nativeElement.files;
    
    for (const key in files) {
      if (!isNaN(parseInt(key, 0))) {
        // console.log('files[key]: ', files[key]);
        if (files[key].name) this.fileName = files[key].name;
        this.files.add(files[key]);
      }
    }

    const fileToSend = (this.file.nativeElement.files.length) ? this.file.nativeElement.files[0] : null;
    
    if (fileToSend) {
      this.applyForm.patchValue({'resume': fileToSend.name});
    }
  }


  cancelUpload() {
    this.filesAdded = false;
    this.files = new Set();
    this.fileName = null;
    this.file.nativeElement.value = "";
  }  


  resolved(captchaResponse: string) { 
    // console.log(`Resolved captcha with response: ${captchaResponse}`);
    // Write your logic here about once human verified what action you want to perform 
    this.applyForm.patchValue({recaptchaField: captchaResponse});
    this.verifiedCaptcha = true;
    // console.log('Contact form: ', this.contactForm);
  }


  // Fired if captcha has error or server issue
  errored(_err) {
    // console.warn(`reCAPTCHA error encountered`);
    this._sharedUtilsService.showEduNotificationWithOptions("reCAPTCHA error encountered: " + _err, 3000, "danger");
  }


  resetFormData() {
    this.applyForm.reset();
  }


  processFile() {
    return new Promise(async(resolve, reject) => {
      const uploadedFiles = await lastValueFrom(this._fileService.uploadToSpecifedFolder(this.files, this.uploadPath));

      const uploadedFile = uploadedFiles && uploadedFiles.length ? uploadedFiles[0] : null;
      // console.log("🚀 ~ file: resource-document-uploader.component.ts ~ line 469 ~ ResourceDocumentUploaderComponent ~ returnnewPromise ~ oneImage", oneImage)

      if (uploadedFile && uploadedFile !== undefined && uploadedFile['body'] && uploadedFile['body'].link) {
        this._sharedUtilsService.showNotification('File upload successful.');
        this.resumePathToUse = uploadedFile['body'].link;
        resolve(uploadedFile['body'].link);
      } else {
        this._sharedUtilsService.showErrorDialog('There was an issue uploading the file attached.');
        resolve(null);
      }
    });
  }


  renameFile(originalFile, newName) {
    return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
    });
  }


  clearForm() {
    this.cancelUpload();
    this.initForm();

    this.formChecked = false;
    this.validForm = false;
    this.isLoadingResults = false;
    this.fileName = null;
    this.resumePathToUse = null;
    this.verifiedCaptcha = false;

    grecaptcha.reset(); // Resets google captcha
  }  


  async updateUserFields(_user: User, _isLoggedInUser: boolean) {
    const userToCheck: User = {..._user};
    userToCheck.first_name = this.currentUser.first_name;
    userToCheck.last_name = this.currentUser.last_name;
    userToCheck.phone = this.currentUser.phone;
    userToCheck.zip = this.currentUser.zip;

    if (this.applicationType === 'apply') {
      userToCheck.address = this.currentUser.address;
      userToCheck.city = this.currentUser.city;
      userToCheck.state = this.currentUser.state;
    }

    const updatedUser = await this._userService.updateUserForApplicant(userToCheck).toPromise();
    if (updatedUser && updatedUser !== undefined) {
      if (_isLoggedInUser) {
        this._authService.setCurrentlyLoggedInUser(updatedUser); // Setting logged user with updated user
        // this.currentUser.next(updatedUser); // Telling parent that userPassed in has been updated
      }

      this.currentApplicant.user = updatedUser;
      await this.saveApplicant();
    } else {
      this.clearForm();
      this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
    }
  }


  createUserForApplicant() {
    // Create user account before submitting
    return new Promise(async (resolve) => {
      let newUser = new User();
      newUser.first_name = this.currentUser.first_name;
      newUser.last_name = this.currentUser.last_name;
      newUser.email = this.currentUser.email;
      newUser.zip = this.currentUser.zip;
      newUser.userType = 'Applicant';
      newUser.phone = this.currentUser.phone;

      if (this.applicationType === 'apply') {
        newUser.address = this.currentUser.address;
        newUser.city = this.currentUser.city;
        newUser.state = this.currentUser.state;
      }

      const savedUser = await this._userService.createUserForApplicant(newUser).toPromise();
      resolve(savedUser);
    });
  }


  userCheck() {
    return new Promise(async(resolve, reject) => {
      if (!this.currentUser || this.currentUser === undefined) {
        // Look for user in db with this email.
        const foundUser = await this._userService.getUserByEmail(this.currentUser.email).toPromise();
  
        if (foundUser && foundUser !== undefined) {
          this.updateUserFields(foundUser, false);
        } else {
          // No user exists, so create one
          const promisedUser = await this.createUserForApplicant();
  
          if (promisedUser && promisedUser !== undefined) {
            this.currentApplicant.user = promisedUser as User;
            await this.saveApplicant();
          } else {
            this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
          }
        }
      } else {
        this.updateUserFields(this.currentUser, true);
      }

      console.log('User check complete...');

      resolve(null);
    });
  }  


  saveApplicant() {
    // Create user account before submitting
    return new Promise(async (resolve) => {
      if (this.validForm) {
        this.currentApplicant.referrerSource = this.referringSrc;

        const createdApplicant = await lastValueFrom(this._applicantService.create(this.currentApplicant));

        if (createdApplicant && createdApplicant !== undefined) {
          this._applicantService.setApplicantToBeViewed(this.currentApplicant); // Setting saved applicant to be used to see if user has account on thank you page
          // this.clearForm();
          this._router.navigate(['/thank-you']); // Go to thank you page
          this._sharedUtilsService.showNotification("Form submission successful");
        } else {
          // this.clearForm();
          // this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
        }
      }

      resolve(null);
    });
  }  


  async submitForm() {
    const userCopy = {...this.currentUser};

    this.isLoadingResults = true;

    if (this.applyForm.invalid) {
      this._sharedUtilsService.showInvalidFormMessage(this.applyForm);
      console.log('Form: ', this.applyForm);
      this.isLoadingResults = false;
      return;
    }

    if (!this.validForm) {
      this._sharedUtilsService.showEduNotificationWithOptions("Invalid fields", 3000, "danger");
      console.log('Form: ', this.applyForm);
      return;
    }

    // Captcha was not selected or has timed out
    if (!this.verifiedCaptcha) {
      this._sharedUtilsService.showEduNotificationWithOptions("Captcha needs to be selected", 3000, "danger");
      // return;
    }

    this.currentApplicant = {...this.applyForm.value};
    console.log('Applicant Data: ', this.currentApplicant)

    const currentFiles = this.file.nativeElement.files; // This.files is always true, so using this to check if file was added below
    const fileCopies = [...currentFiles];

    // const emailResponse = await lastValueFrom(this._emailSenderService.contactUs(this.applyData));
    
    // if (emailResponse != undefined && !emailResponse['error']) {
    //   this._sharedUtilsService.showNotification('Thank you. Your form was successfully sent and we will contact you as soon as possible.');
    //   this.resetFormData();
    //   // this.router.navigate(['/home']);
    // } else if (emailResponse != undefined && emailResponse['error']) {
    //   this._sharedUtilsService.showErrorDialog(emailResponse['error']);
    // } else {
    //   this._sharedUtilsService.showErrorDialog('There was an issue sending this email. Please try again later.');
    // }

    if (this.validForm) {
      let processedFile = null;
      let fixedFileName = null;

      // console.log('Valid form...');

      if (currentFiles.length && this.filesAdded && (!this.currentApplicant.resume_path || this.currentApplicant.resume_path === '')) {
        this.isLoadingResults = true;

        processedFile = await this.processFile();

        // console.log('processedFile: ', processedFile);
        // console.log('Files: ', this.files);

        const fileToSend = (fileCopies && fileCopies.length) ? fileCopies[0] : null;

        if (fileToSend) {
          // console.log('fileToSend: ', fileToSend);

          fixedFileName = processedFile.split('/').pop();
          const fileCopy = this.renameFile(fileToSend, fixedFileName);

          await this._fileService.uploadAsIsToYeehro(fixedFileName, fileCopy).toPromise();
          // console.log('uploadToYeehro: ', uploadToYeehro);
        }

        if (processedFile && processedFile !== undefined) {
          this.currentApplicant.resume_path = processedFile;
          await this.userCheck();
        }
      } else {
        this.isLoadingResults = true;
        await this.userCheck();
      }

      const currentComments = this.currentApplicant.message;
      this.currentApplicant.comments = `Quick Apply: ${currentComments}`;

      const _yeehroDetails = {
        applicant: this.currentApplicant,
        user: userCopy,
        referrerSource: this.referringSrc,
        file: {
          name: fixedFileName,
          link: `/uploads/resumes/${fixedFileName}`
        }
      };

      console.log('**** Sending applicant to yeehro: ', _yeehroDetails);
      await lastValueFrom(this._applicantService.sendToYeehro(_yeehroDetails));

      try {
        gtag('event', 'job_application_submitted', {
          event_category: 'Job Application',
          event_label: this.currentApplicant?.profession || 'Unknown',
          application_type: 'quick_apply'
        });
      } catch (_e) {}

      this._router.navigate(['/thank-you']); // Go to thank you page
      this._sharedUtilsService.showNotification("Form submission successful");      
    } else {
      this._sharedUtilsService.showEduNotificationWithOptions("Form is invalid", 3000, "danger");
    }

    this.isLoadingResults = false;
  }  
}
